import { __assign, __read, __rest, __spread } from "tslib";
import React, { useRef, forwardRef, useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { BubbleContent } from "../bubble";
import { Popover } from "../popover";
import { getPrecision } from "../_util/get-precision";
import { useConfig } from "../_util/config-context";
import { noop } from "../_util/noop";
import { flatten } from "../_util/flatten";
import { SliderHandle } from "./SliderHandle";
var defaultFormatter = function (number) { return number; };
var sort = function (arr) {
    return arr.sort(function (a, b) { return a - b; });
};
var getEvent = function (event) {
    if ("targetTouches" in event) {
        return event.targetTouches[0];
    }
    return event;
};
export var SliderTrack = React.forwardRef(function SliderTrack(_a, ref) {
    var _b, _c, _d;
    var value = _a.value, _e = _a.min, min = _e === void 0 ? 0 : _e, max = _a.max, _f = _a.step, step = _f === void 0 ? 1 : _f, range = _a.range, _g = _a.multiRangeMode, multiRangeMode = _g === void 0 ? false : _g, _h = _a.dotValues, dotValues = _h === void 0 ? [] : _h, rangeMode = _a.rangeMode, _j = _a.marks, marks = _j === void 0 ? [] : _j, disabled = _a.disabled, markValueOnly = _a.markValueOnly, enableTrackTip = _a.enableTrackTip, after = _a.after, vertical = _a.vertical, _k = _a.onUpdate, onUpdate = _k === void 0 ? noop : _k, _l = _a.onChange, onChange = _l === void 0 ? noop : _l, _m = _a.tipFormatter, tipFormatter = _m === void 0 ? defaultFormatter : _m, className = _a.className, props = __rest(_a, ["value", "min", "max", "step", "range", "multiRangeMode", "dotValues", "rangeMode", "marks", "disabled", "markValueOnly", "enableTrackTip", "after", "vertical", "onUpdate", "onChange", "tipFormatter", "className"]);
    var classPrefix = useConfig().classPrefix;
    var offsetKey = vertical ? "bottom" : "left";
    var boundingKey = vertical ? "height" : "width";
    var _o = __read(value, 2), startValue = _o[0], endValue = _o[1];
    var precision = useMemo(function () { return getPrecision(step); }, [step]);
    // 定位相关 hooks
    var startRef = useRef(null);
    var endRef = useRef(null);
    // 滑轨相关 hooks
    var scheduleRef = useRef(null);
    var mouseElement = useRef(null);
    var _p = __read(useState(null), 2), mousePosition = _p[0], setMousePosition = _p[1];
    // 滑块相关 hooks
    var positionRef = useRef(0);
    var dragOffsetRef = useRef(0);
    var innerScheduleRef = useRef(null);
    var _q = __read(useState(false), 2), dragging = _q[0], setDragging = _q[1];
    // const [hovering, setHovering] = useState(false);
    useEffect(function () { return removeDocumentEvents; }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // 拖动过程中值
    var _r = __read(useState(null), 2), interactiveValue = _r[0], setInteractiveValue = _r[1];
    var interactivePosRef = useRef(null);
    var displayDotValues = getValidDotAndRanges(dotValues, range).filter(function (value) { return typeof value === "number"; });
    var total = Math.abs(max - min);
    var proportions = !rangeMode
        ? getProportions()
        : [sort(getProportions())];
    function calcProportion(num) {
        return ((num - min) / total) * 100;
    }
    function getProportions() {
        if (!rangeMode && !multiRangeMode) {
            return [
                [
                    0,
                    calcProportion(interactiveValue === null ? endValue : interactiveValue),
                ],
            ];
        }
        // 多段模式
        if (!rangeMode && multiRangeMode) {
            var realRanges = getValidDotAndRanges(dotValues, range)
                .filter(function (item) { return Array.isArray(item); })
                .map(function (item) {
                return [calcProportion(item[0]), calcProportion(item[1])];
            });
            return realRanges;
        }
        // rangeMode
        if (interactiveValue === null || interactivePosRef.current === null) {
            return [calcProportion(startValue), calcProportion(endValue)];
        }
        return interactivePosRef.current === "start"
            ? [calcProportion(interactiveValue), calcProportion(endValue)]
            : [calcProportion(startValue), calcProportion(interactiveValue)];
    }
    function addDocumentMouseEvents() {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("touchmove", handleMouseMove, { passive: false });
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchend", handleMouseUp, { passive: false });
    }
    function removeDocumentEvents() {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("touchmove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchend", handleMouseUp);
    }
    function handleMouseUp(event) {
        event.preventDefault();
        setDragging(false);
        removeDocumentEvents();
        var value = getValueByPosition(positionRef.current);
        if (!rangeMode) {
            onChange([0, value], { event: event });
        }
        else {
            onChange(sort(interactivePosRef.current === "start"
                ? [value, endValue]
                : [startValue, value]), { event: event });
        }
        if (rangeMode) {
            interactivePosRef.current = null;
        }
        setInteractiveValue(null);
    }
    function handleMouseMove(event) {
        if (event.cancelable) {
            event.preventDefault();
        }
        if (disabled) {
            return;
        }
        var position = vertical ? getEvent(event).pageY : getEvent(event).pageX;
        var value = getValueByPosition(position - dragOffsetRef.current);
        if (getValueByPosition(positionRef.current) !== value) {
            positionRef.current = position;
            setInteractiveValue(value);
            onUpdate(sort(interactivePosRef.current === "start"
                ? [value, endValue]
                : [startValue, value]));
        }
        if (innerScheduleRef.current) {
            innerScheduleRef.current();
        }
    }
    function handleMouseDown(event) {
        if (disabled) {
            return;
        }
        var position = vertical ? getEvent(event).pageY : getEvent(event).pageX;
        var value = getValueByPosition(position);
        if (rangeMode) {
            interactivePosRef.current =
                Math.abs(value - startValue) < Math.abs(value - endValue)
                    ? "start"
                    : "end";
        }
        setInteractiveValue(value);
        onUpdate(sort(interactivePosRef.current === "start"
            ? [value, endValue]
            : [startValue, value]));
        // 滑块拖动相关事件
        setDragging(true);
        positionRef.current = position;
        dragOffsetRef.current = 0;
        removeDocumentEvents();
        addDocumentMouseEvents();
    }
    function handleMouseLeave(event) {
        event.preventDefault();
        setMousePosition(null);
    }
    function getStartPos() {
        if (!startRef.current) {
            return 0;
        }
        var coords = startRef.current.getBoundingClientRect();
        return ((vertical ? window.pageYOffset : window.pageXOffset) + coords[offsetKey]);
    }
    function getEndPos() {
        if (!endRef.current) {
            return 0;
        }
        var coords = endRef.current.getBoundingClientRect();
        return ((vertical ? window.pageYOffset : window.pageXOffset) + coords[offsetKey]);
    }
    function getValidDotAndRanges(dotValues, range) {
        return __spread(dotValues, range).sort(function (a, b) {
            if (Array.isArray(a) && Array.isArray(b)) {
                return a[0] > b[0] || (a[0] === b[0] && a[1] < b[1]) ? 1 : -1;
            }
            if (Array.isArray(a) && typeof b === "number") {
                return a[0] > b ? 1 : -1;
            }
            if (typeof a === "number" && Array.isArray(b)) {
                return a > b[0] ? 1 : -1;
            }
            return a > b ? 1 : -1;
        })
            .reduce(function (pre, current) {
            var len = pre.length;
            if (len === 0) {
                return [current];
            }
            var tailItem = pre[len - 1];
            // 最后元素和遍历元素均为数组
            if (Array.isArray(tailItem) && Array.isArray(current)) {
                if (tailItem[1] >= current[0] && tailItem[1] <= current[1]) {
                    pre[len - 1] = [tailItem[0], current[1]];
                    return pre;
                }
                if (tailItem[1] >= current[0] && tailItem[1] > current[1]) {
                    return pre;
                }
                pre.push(current);
            }
            if (Array.isArray(tailItem) && typeof current === "number") {
                if (tailItem[1] >= current) {
                    return pre;
                }
                pre.push(current);
            }
            if (typeof tailItem === "number" && Array.isArray(current)) {
                if (tailItem === current[0]) {
                    pre[len - 1] = current;
                    return pre;
                }
                pre.push(current);
            }
            if (typeof tailItem === "number" && typeof current === "number") {
                if (tailItem !== current) {
                    pre.push(current);
                }
            }
            return pre;
        }, []);
    }
    function getValueByPosition(position) {
        // 垂直时上下相反
        var _a = __read(sort([getStartPos(), getEndPos()]), 2), start = _a[0], end = _a[1];
        var posRange = end - start;
        if (position < start) {
            position = start; // eslint-disable-line no-param-reassign
        }
        if (position > end) {
            position = end; // eslint-disable-line no-param-reassign
        }
        var proportion = (vertical ? end - position : position - start) / posRange;
        return getValueByProportion(proportion);
    }
    function getValueByProportion(proportion) {
        var pow = Math.pow(10, precision);
        var value = proportion * total;
        value -= ((value * pow) % (step * pow)) / pow;
        value += min;
        if (markValueOnly && marks.length) {
            // eslint-disable-next-line prefer-destructuring
            value = marks.reduce(function (prev, cur) {
                return Math.abs(cur.value - value) < Math.abs(prev.value - value)
                    ? cur
                    : prev;
            }, marks[0]).value;
        }
        // 点模式的range可为多段
        if (!rangeMode) {
            var valueRanges = getValidDotAndRanges(dotValues, range);
            var isInRangeOrDotValues = valueRanges.reduce(function (pre, current) {
                return (pre ||
                    (Array.isArray(current) &&
                        value >= current[0] &&
                        value <= current[1]) ||
                    (typeof current === "number" && current === value));
            }, false);
            if (!isInRangeOrDotValues && valueRanges.length) {
                value = flatten(valueRanges).reduce(function (pre, current) {
                    return Math.abs(value - pre) < Math.abs(value - current)
                        ? pre
                        : current;
                });
            }
        }
        else {
            var _a = __read(range, 2), allowedStart = _a[0], allowedEnd = _a[1];
            if (value < allowedStart) {
                value = allowedStart;
            }
            if (value > allowedEnd) {
                value = allowedEnd;
            }
        }
        return parseFloat(value.toFixed(precision));
    }
    function handleMouseMoveForPopover(event) {
        if (disabled) {
            return;
        }
        var position = vertical ? getEvent(event).pageY : getEvent(event).pageX;
        setMousePosition(position);
        setMouseElement(event.nativeEvent);
        if (scheduleRef.current) {
            scheduleRef.current();
        }
    }
    function setMouseElement(event) {
        var clientX = Math.round(getEvent(event).clientX);
        var clientY = startRef.current.getBoundingClientRect().top;
        mouseElement.current = {
            clientWidth: 0,
            clientHeight: 0,
            getBoundingClientRect: function () {
                return ({
                    left: clientX,
                    top: clientY,
                    right: clientX,
                    bottom: clientY,
                    width: 0,
                    height: 0,
                });
            },
            // IE10 下 Popper 读取这两个值计算
            scrollTop: 0,
            scrollLeft: 0,
        };
    }
    return (React.createElement("div", __assign({ ref: ref, className: classNames(classPrefix + "-slider", className, (_b = {},
            _b[classPrefix + "-slider--vertical"] = vertical,
            _b["is-disabled"] = disabled,
            _b[classPrefix + "-slider--time"] = !rangeMode && multiRangeMode,
            _b)) }, props),
        React.createElement(Popover, { visible: !!enableTrackTip && !disabled && !!mousePosition, referenceElement: mouseElement.current, placement: vertical ? "left" : "top", placementOffset: 16, transitionTimeout: 0, overlayStyle: {
                pointerEvents: "none",
            }, overlay: function (_a) {
                var placement = _a.placement, scheduleUpdate = _a.scheduleUpdate;
                scheduleRef.current = scheduleUpdate;
                var tips = tipFormatter(getValueByPosition(mousePosition));
                return (tips !== null &&
                    !!mousePosition && (React.createElement(BubbleContent, { placement: placement }, tips)));
            } },
            React.createElement("div", { className: classPrefix + "-slider__content", onMouseDown: handleMouseDown, onMouseMove: handleMouseMoveForPopover, onMouseLeave: handleMouseLeave, onTouchStart: handleMouseDown, onTouchMove: handleMouseMoveForPopover, onTouchEnd: handleMouseLeave, onDragStart: function (e) { return e.preventDefault(); } },
                React.createElement(StartFlag, { style: (_c = {}, _c[offsetKey] = 0, _c), ref: startRef }),
                React.createElement("div", { className: classPrefix + "-slider__bar" }, proportions &&
                    proportions.map(function (proportion, index) {
                        var _a;
                        return (React.createElement("div", { key: index, className: classPrefix + "-slider__track", style: (_a = {},
                                _a[boundingKey] = proportion[1] - proportion[0] + "%",
                                _a[offsetKey] = proportion[0] + "%",
                                _a) }));
                    })),
                rangeMode && (React.createElement(SliderHandle, { vertical: vertical, classPrefix: classPrefix, value: 
                    // eslint-disable-next-line no-nested-ternary
                    interactiveValue !== null
                        ? interactivePosRef.current === "start"
                            ? endValue
                            : startValue
                        : startValue, offset: 
                    // eslint-disable-next-line no-nested-ternary
                    interactiveValue !== null
                        ? interactivePosRef.current === "start"
                            ? calcProportion(endValue)
                            : calcProportion(startValue)
                        : calcProportion(startValue), tipFormatter: tipFormatter, enableTip: !enableTrackTip })),
                React.createElement(SliderHandle, { vertical: vertical, classPrefix: classPrefix, value: interactiveValue !== null ? interactiveValue : endValue, offset: interactiveValue !== null
                        ? calcProportion(interactiveValue)
                        : calcProportion(endValue), tipFormatter: tipFormatter, dragging: dragging, enableTip: !enableTrackTip }),
                !rangeMode &&
                    multiRangeMode &&
                    displayDotValues.length > 0 &&
                    displayDotValues.map(function (value) {
                        var _a;
                        return (React.createElement("div", { className: classPrefix + "-slider__dot", key: value, style: (_a = {}, _a[offsetKey] = ((value - min) / total) * 100 + "%", _a) }));
                    }),
                React.createElement("div", { className: classPrefix + "-slider__mark" }, marks
                    .filter(function (_a) {
                    var value = _a.value;
                    return value >= min && value <= max;
                })
                    .map(function (_a) {
                    var _b;
                    var value = _a.value, label = _a.label;
                    return (React.createElement("span", { key: value, className: classPrefix + "-slider__text", style: (_b = {},
                            _b[offsetKey] = ((value - min) / total) * 100 + "%",
                            _b.userSelect = "none",
                            _b.pointerEvents = "none",
                            _b.cursor = "default",
                            _b) }, label || value));
                })),
                React.createElement(EndFlag, { style: (_d = {}, _d[offsetKey] = "100%", _d), ref: endRef }))),
        React.createElement("div", { className: classPrefix + "-slider__extend" }, after)));
});
SliderTrack.displayName = "SliderTrack";
var StartFlag = forwardRef(function (_a, ref) {
    var style = _a.style;
    return (React.createElement("span", { ref: ref, style: __assign({ position: "absolute", visibility: "hidden" }, style) }));
});
StartFlag.displayName = "SliderTrackStartFlag";
var EndFlag = forwardRef(function (_a, ref) {
    var style = _a.style;
    return (React.createElement("span", { ref: ref, style: __assign({ position: "absolute", visibility: "hidden" }, style) }));
});
EndFlag.displayName = "SliderTrackEndFlag";
//# sourceMappingURL=SliderTrack.js.map